import { decodeToken } from "@/lib/jwt";
function isRecord(value) {
    return !!value && typeof value === "object" && !Array.isArray(value);
}
function normalizeRoleName(role) {
    var _a;
    let r = role.trim().toLowerCase();
    // Common patterns from auth systems: ROLE_DOCTOR / role:doctor / role/doctor
    r = r.replace(/^role[_:/-]+/, "");
    r = r.replace(/^roles?[_:/-]+/, "");
    // If the role is namespaced, keep the last segment.
    if (/[/:]/.test(r)) {
        const parts = r.split(/[/:]/).filter(Boolean);
        r = (_a = parts[parts.length - 1]) !== null && _a !== void 0 ? _a : r;
    }
    // Collapse whitespace / underscores / dashes to a single dash (future-proofing).
    r = r.replace(/\s+/g, " ");
    return r;
}
function normalizeRoleValue(value) {
    if (!value)
        return [];
    // roles: ["doctor", "ROLE_DOCTOR"]
    if (Array.isArray(value)) {
        return value.flatMap((v) => normalizeRoleValue(v));
    }
    // roles: "doctor"
    if (typeof value === "string") {
        const n = normalizeRoleName(value);
        return n ? [n] : [];
    }
    // roles: [{ name: "doctor" }] / [{ role: "doctor" }] / [{ slug: "doctor" }]
    if (isRecord(value)) {
        const maybeName = (typeof value.name === "string" && value.name) ||
            (typeof value.role === "string" && value.role) ||
            (typeof value.slug === "string" && value.slug);
        if (maybeName) {
            return normalizeRoleValue(maybeName);
        }
        // roles: { doctor: true, patient: false }  (rare, but easy to support)
        const truthyKeys = Object.entries(value)
            .filter(([, v]) => v === true)
            .map(([k]) => k);
        if (truthyKeys.length) {
            return truthyKeys.flatMap((k) => normalizeRoleValue(k));
        }
    }
    return [];
}
export function extractRolesFromMeResponse(data) {
    // We intentionally accept unknown backend shapes here.
    // Known patterns we support:
    // - { roles: [...] }
    // - { user: { roles: [...] } }
    // - { claims: { roles: [...] } }
    if (!isRecord(data))
        return [];
    const candidates = [
        data.roles,
        data.user && isRecord(data.user) ? data.user.roles : undefined,
        data.claims && isRecord(data.claims) ? data.claims.roles : undefined,
    ];
    const roles = candidates.flatMap((c) => normalizeRoleValue(c));
    // De-dupe while preserving order.
    return Array.from(new Set(roles));
}
export function isDoctorFromMeResponse(data) {
    if (isRecord(data)) {
        if (data.is_doctor === true)
            return true;
        if (isRecord(data.user) && data.user.is_doctor === true)
            return true;
    }
    return extractRolesFromMeResponse(data).includes("doctor");
}
/** Normalized role string equals one of the admin-like roles. */
function isAdminNormalizedRole(n) {
    return (n === "admin" ||
        n === "administrator" ||
        n === "superadmin" ||
        n === "super_admin" ||
        n === "super-admin");
}
/** True if JWT /me roles include admin (after normalization). */
export function isAdminFromRoles(roles) {
    if (!(roles === null || roles === void 0 ? void 0 : roles.length))
        return false;
    return roles.some((r) => isAdminNormalizedRole(normalizeRoleName(String(r))));
}
/** `/v1/me`-style payload: explicit admin flags some backends use. */
export function isAdminFromMeResponseBody(data) {
    if (!isRecord(data))
        return false;
    if (data.is_admin === true || data.isAdmin === true)
        return true;
    if (isRecord(data.user)) {
        if (data.user.is_admin === true || data.user.isAdmin === true)
            return true;
    }
    return isAdminFromRoles(extractRolesFromMeResponse(data));
}
/**
 * Admin check for UI redirects: `/me` roles when loaded, otherwise JWT `roles` claim.
 */
export function isEffectiveAdmin(token, rolesFromMe) {
    if (isAdminFromRoles(rolesFromMe))
        return true;
    if (!token)
        return false;
    const claims = decodeToken(token);
    return isAdminFromRoles(claims === null || claims === void 0 ? void 0 : claims.roles);
}
